<template>
  <div class="bullet-image-picker">
    <div class="thumb-frame" :class="{ empty: !selectedImage }">
      <img
        v-if="selectedImage && selectedImage.imageUrl"
        :src="selectedImage.imageUrl"
        :alt="selectedImage.name || selectedImage.imageIdentifier"
      />
      <div v-else class="thumb-placeholder">
        <i class="fas fa-image"></i>
        <span>Pick an icon</span>
      </div>
    </div>

    <select
      class="form-control"
      :value="imageIdentifier"
      :disabled="disabled"
      @change="onChange"
    >
      <option :value="''" disabled>{{ placeholderLabel }}</option>
      <option
        v-for="img in filteredImages"
        :key="img.imageIdentifier"
        :value="img.imageIdentifier"
      >
        {{ optionLabel(img) }}
      </option>
    </select>

    <p v-if="filteredImages.length === 0" class="hint">
      No APPROVED <strong>bullet icons</strong> yet. Upload a 1024×1024 image and
      tag it as <strong>Bullet icon</strong> in the Images tab.
    </p>
  </div>
</template>

<script>
export default {
  name: 'RetentionBulletImagePicker',
  props: {
    imageIdentifier: { type: String, default: '' },
    disabled: { type: Boolean, default: false }
  },
  computed: {
    approvedImages() {
      return this.$store.getters['retention/approvedImages'] || [];
    },
    // Bullet slot uses Apple's BULLET_POINT (1024×1024). Only `usage === 'bullet'`
    // images match that physical format — main images would fail Apple validation
    // if forced into a bullet slot, so this filter is hard (no override toggle).
    filteredImages() {
      return this.approvedImages.filter((img) => img.usage === 'bullet');
    },
    selectedImage() {
      if (!this.imageIdentifier) return null;
      return (
        this.approvedImages.find((i) => i.imageIdentifier === this.imageIdentifier) || null
      );
    },
    placeholderLabel() {
      if (this.filteredImages.length === 0) return 'No approved bullet icons';
      return 'Pick an APPROVED bullet icon';
    }
  },
  methods: {
    optionLabel(img) {
      const name = img.name || `${img.imageIdentifier.slice(0, 8)}…`;
      return name;
    },
    onChange(e) {
      this.$emit('update:imageIdentifier', e.target.value);
    }
  }
};
</script>

<style scoped>
.bullet-image-picker {
  display: flex;
  flex-direction: column;
  gap: 0.55rem;
  width: 160px;
  min-width: 160px;
}

.thumb-frame {
  width: 100%;
  aspect-ratio: 1 / 1;
  border-radius: 8px;
  overflow: hidden;
  background: rgba(0, 0, 0, 0.3);
  border: 1px solid rgba(255, 255, 255, 0.08);
  display: flex;
  align-items: center;
  justify-content: center;
}

.thumb-frame.empty {
  border: 1px dashed rgba(255, 255, 255, 0.18);
  background: rgba(255, 255, 255, 0.02);
}

.thumb-frame img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.thumb-placeholder {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.4rem;
  color: rgba(255, 255, 255, 0.45);
  font-size: 0.78rem;
}

.thumb-placeholder i {
  font-size: 1.4rem;
  color: rgba(255, 255, 255, 0.35);
}

.form-control {
  width: 100%;
  padding: 0.4rem 0.6rem;
  padding-right: 1.6rem;
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 6px;
  background: rgba(255, 255, 255, 0.04);
  color: rgba(255, 255, 255, 0.92);
  font-size: 0.78rem;
  appearance: none;
  -webkit-appearance: none;
  background-image: url("data:image/svg+xml;charset=UTF-8,%3csvg xmlns='http://www.w3.org/2000/svg' width='12' height='8' viewBox='0 0 12 8'%3e%3cpath fill='%23ffffff' fill-opacity='0.5' d='M6 8L0 0h12z'/%3e%3c/svg%3e");
  background-repeat: no-repeat;
  background-position: right 0.6rem center;
}

.form-control option {
  background: #1a2332;
  color: #fff;
}

.form-control:focus {
  outline: none;
  border-color: #03B3DA;
}

.hint {
  margin: 0;
  font-size: 0.72rem;
  color: rgba(255, 255, 255, 0.5);
  line-height: 1.35;
}

.hint strong {
  color: rgba(255, 255, 255, 0.78);
  font-weight: 600;
}
</style>
